import { useEffect, useMemo, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { useKeycloak } from '@react-keycloak/web';
import { createPortal } from 'react-dom';
import { $user, type User } from '@/models/user';
import { ED_URL } from '@/components/UI/Layouts/Main/mainNavigation';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { UserGroup } from '@/types/groups';

const getUserInitials = (user?: User | null) => {
  if (!user) return '?';

  const given = user.given_name?.trim();
  const family = user.family_name?.trim();

  if (given && family) {
    return `${given[0]}${family[0]}`.toUpperCase();
  }

  const nameParts = user.name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }
  if (nameParts.length === 1) {
    return nameParts[0][0].toUpperCase();
  }

  return (user.email?.[0] ?? '?').toUpperCase();
};

export const UserHeaderSection: React.FC = () => {
  const { keycloak } = useKeycloak();
  const [isActive, setIsActive] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const userProfileRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const user = useUnit($user);
  const initials = useMemo(() => getUserInitials(user), [user]);

  const canAccessAdmin = Boolean(
    user?.groups?.some(
      (group) =>
        group === UserGroup.Applications ||
        group === UserGroup.UserGroups ||
        group === UserGroup.Chats,
    ),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userProfileRef.current?.contains(target) || userButtonRef.current?.contains(target)) {
        return;
      }
      setIsActive(false);
    };

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive]);

  useEffect(() => {
    if (!showLogoutModal) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowLogoutModal(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutModal]);

  const handleLogout = async () => {
    try {
      await keycloak.logout();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="relative">
      <button
        ref={userButtonRef}
        type="button"
        className="flex items-center gap-2 cursor-pointer bg-transparent border-0 p-0"
        aria-haspopup="true"
        aria-expanded={isActive}
        aria-controls="user-menu"
        onClick={() => setIsActive((open) => !open)}
      >
        <div className="max-sm:hidden flex flex-col items-end max-w-[116px] sm:max-w-[160px] lg:max-w-none">
          <span className="font-medium text-sm md:text-base text-gray-800 text-right truncate max-w-full">
            {user?.name}
          </span>
          <span className="text-xs text-gray-500 truncate max-w-full">{user?.email}</span>
        </div>
        <div className="w-9 h-9 bg-black text-white rounded-full flex items-center justify-center text-lg cursor-pointer shrink-0">
          {initials}
        </div>
      </button>

      {isActive && (
        <div
          ref={userProfileRef}
          id="user-menu"
          className="flex flex-col absolute right-0 top-full mt-2 bg-white shadow-lg rounded-lg py-2 min-w-[120px] z-50 border"
        >
          <div className="sm:hidden flex flex-col px-4 py-2">
            <span className="text-sm md:text-base font-semibold text-gray-800 text-right truncate max-w-full">
              {user?.name}
            </span>
            <span className="text-xs text-gray-500 truncate max-w-full">{user?.email}</span>
          </div>
          <a
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer text-left"
            href={`${ED_URL}/my-profile`}
          >
            Мой профиль
          </a>
          {canAccessAdmin && (
            <Link
              to={ROUTES.ADMIN.BASE}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer text-left"
            >
              Admin
            </Link>
          )}
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer text-left"
            onClick={() => {
              setIsActive(false);
              setShowLogoutModal(true);
            }}
          >
            Выход
          </button>
        </div>
      )}

      {showLogoutModal &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-modal-title"
            onClick={() => setShowLogoutModal(false)}
          >
            <div
              className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full"
              onClick={(event) => event.stopPropagation()}
            >
              <div id="logout-modal-title" className="text-lg font-semibold mb-4 text-gray-900">
                Вы уверены, что хотите выйти?
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Нет
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#631d76] text-white rounded hover:bg-[#674d81]"
                  onClick={handleLogout}
                >
                  Да
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
